import { createTheme } from '@mui/material/styles'

/**
 * "Warm Studio": el lienzo es papel, no pantalla.
 *
 * Todo el sistema se apoya en un crema cálido y una tinta casi negra. El color
 * saturado sigue siendo señal —nunca decoración—, pero ahora vive sobre un
 * fondo con temperatura, así que un rojo de retraso se lee como una marca de
 * plumón sobre una hoja y no como una alerta de sistema operativo.
 *
 * Tres tipografías con tres oficios, y ninguno se pisa con otro:
 *   · Instrument Serif DICE     — titulares y cifras grandes. Alto contraste,
 *                                 ancho angosto: aguanta 4rem sin gritar.
 *   · Archivo EXPLICA           — interfaz, botones, texto corrido.
 *   · IBM Plex Mono CUENTA      — fechas, conteos, etiquetas, ejes.
 * La regla operativa: si es un número o una fecha dentro de una fila, va en
 * mono; si es una cifra que encabeza algo, va en serif.
 */

/* ── Lienzo ──────────────────────────────────────────────────────────────── */
export const CANVAS = '#FAF7F3'
export const PAPER = '#FFFDFA'
export const BAND = '#F1ECE3'
export const BAND_SOFT = '#F6F2EB'
export const INK = '#151210'
export const INK_SOFT = '#6C645B'
export const INK_FAINT = '#9C9287'
export const LINE = '#E5DED4'
export const LINE_SOFT = '#EFE9E0'

/** Un solo acento de marca. Se usa poco, y por eso se ve. */
export const ACCENT = '#E8462A'
export const ACCENT_SOFT = '#FFE7E1'

/**
 * Tintes de sección: lavados al 5–8 % para separar zonas sin dibujar cajas.
 * Son fondos, nunca estados — por eso están tan lejos de los pigmentos de abajo.
 */
export const TINT = {
  blush: '#FBEDE8',
  sage: '#EAF0E0',
  sky: '#E8F1F6',
  plum: '#F0EBF9',
  sand: '#F6EFE2',
}

/** Relieve de calcomanía: desplazamiento sólido, sin difuminar. */
export const STICKER = '0 2px 0 rgba(21, 18, 16, 0.05)'
export const STICKER_HOVER = '0 4px 0 rgba(21, 18, 16, 0.08)'

export const FONT_SERIF = '"Instrument Serif", "Iowan Old Style", Georgia, serif'
export const FONT_DISPLAY = '"Archivo", "Helvetica Neue", Arial, sans-serif'
export const FONT_MONO = '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace'

/**
 * Pigmentos, no colores de sistema: cada estado está desaturado lo justo para
 * convivir con el crema sin apagarse.
 */
export const STATUS_COLOR: Record<string, string> = {
  upcoming: '#A6998A',
  expected_today: '#DE8A1B',
  available: '#3091B5',
  delayed: '#DF412A',
  validation_pending: '#7C5CD6',
  validated: '#3F8F5B',
  action_required: '#C0221A',
  unconfigured: '#CFC5B7',
  check_failed: '#D2A200',
  not_applicable: '#E7E0D6',
  // Verde salvia: está resuelto, pero no se verificó archivo por archivo —
  // no debe competir visualmente con una detección real.
  baseline: '#8FB996',
}

export const RISK_COLOR: Record<string, string> = {
  on_track: '#3F8F5B',
  at_risk: '#DE8A1B',
  breached: '#DF412A',
  // Cerrado: gris, no verde. Verde compite por la mirada con lo que sí va en
  // marcha, y un ciclo terminado sólo tiene que dejar de pedir atención.
  done: '#8A8079',
}

export const SEVERITY_COLOR: Record<string, string> = {
  info: '#3091B5',
  warning: '#DE8A1B',
  critical: '#DF412A',
}

/** Etiqueta mono en versalitas: el tono "archivo" de todo el tablero. */
export const monoLabel = {
  fontFamily: FONT_MONO,
  fontSize: '0.625rem',
  fontWeight: 500,
  letterSpacing: '0.14em',
  textTransform: 'uppercase' as const,
  color: INK_FAINT,
}

/** Cifra grande de encabezado: serif, casi sin interletra, tinta plena. */
export const serifNumber = {
  fontFamily: FONT_SERIF,
  fontWeight: 400,
  letterSpacing: '-0.015em',
  lineHeight: 1,
  color: INK,
}

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: ACCENT, contrastText: '#FFFDFA' },
    secondary: { main: '#3091B5' },
    success: { main: '#3F8F5B' },
    warning: { main: '#DE8A1B' },
    error: { main: '#DF412A' },
    info: { main: '#3091B5' },
    background: { default: CANVAS, paper: PAPER },
    text: { primary: INK, secondary: INK_SOFT, disabled: INK_FAINT },
    divider: LINE,
  },

  typography: {
    fontFamily: FONT_DISPLAY,
    // h1 es la marca: se queda en Archivo, porque es interfaz, no editorial.
    h1: { fontSize: '1.125rem', fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.1 },
    // h2 es el título de cada tarjeta: serif grande y suelto contra el mono de
    // su bajada. Es el contraste que le quita al tablero el aire de formulario.
    h2: {
      fontFamily: FONT_SERIF,
      fontSize: '1.5rem',
      fontWeight: 400,
      letterSpacing: '-0.005em',
      lineHeight: 1.15,
    },
    h3: { fontSize: '0.9375rem', fontWeight: 600, letterSpacing: '-0.012em', lineHeight: 1.35 },
    body1: { fontSize: '0.9375rem', lineHeight: 1.55 },
    body2: { fontSize: '0.875rem', lineHeight: 1.55 },
    button: { fontWeight: 600, letterSpacing: '-0.005em' },
    caption: { ...monoLabel, fontSize: '0.6875rem', letterSpacing: '0.06em', textTransform: 'none' },
    overline: monoLabel,
  },

  shape: { borderRadius: 14 },

  transitions: {
    easing: { easeOut: 'cubic-bezier(0.2, 0.8, 0.2, 1)' },
    duration: { enteringScreen: 220, leavingScreen: 180 },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: CANVAS, WebkitFontSmoothing: 'antialiased' },
      },
    },

    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: PAPER,
          border: `1px solid ${LINE}`,
          borderRadius: 18,
          boxShadow: STICKER,
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: FONT_MONO,
          fontWeight: 500,
          fontSize: '0.6875rem',
          letterSpacing: '0.04em',
          height: 24,
          borderRadius: 999,
        },
        outlined: { borderColor: LINE, color: INK_SOFT },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 999,
          fontWeight: 600,
          paddingInline: 16,
          transition: 'transform 160ms cubic-bezier(0.2,0.8,0.2,1), box-shadow 160ms',
          '&:active': { transform: 'translateY(1px)' },
        },
        contained: { boxShadow: STICKER, '&:hover': { boxShadow: STICKER_HOVER } },
        outlined: {
          borderColor: LINE,
          color: INK,
          backgroundColor: PAPER,
          '&:hover': { borderColor: INK_FAINT, backgroundColor: BAND_SOFT },
        },
        text: { color: INK_SOFT, '&:hover': { backgroundColor: BAND } },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: 999, '&:hover': { backgroundColor: BAND } },
      },
    },

    MuiTooltip: {
      defaultProps: { arrow: true },
      styleOverrides: {
        tooltip: {
          backgroundColor: INK,
          color: '#FDFBF7',
          fontFamily: FONT_MONO,
          fontSize: '0.6875rem',
          lineHeight: 1.6,
          letterSpacing: '0.01em',
          borderRadius: 10,
          padding: '8px 10px',
          maxWidth: 320,
        },
        arrow: { color: INK },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: LINE_SOFT, fontSize: '0.8125rem', fontFamily: FONT_DISPLAY },
        head: {
          ...monoLabel,
          fontSize: '0.625rem',
          color: INK_SOFT,
          backgroundColor: BAND,
          borderBottom: `1px solid ${LINE}`,
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 14, border: '1px solid', fontSize: '0.875rem', alignItems: 'center' },
        standardWarning: { backgroundColor: '#FDF3E2', borderColor: '#F0DCBB', color: '#7A4E05' },
        standardError: { backgroundColor: '#FDEAE6', borderColor: '#F5CDC5', color: '#8F2317' },
        standardSuccess: { backgroundColor: '#E9F4EC', borderColor: '#C9E3D2', color: '#245C39' },
        standardInfo: { backgroundColor: '#E7F2F7', borderColor: '#C4DEE9', color: '#1B5872' },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: { borderRadius: 0, borderLeft: `1px solid ${LINE}`, boxShadow: 'none' },
      },
    },

    MuiAccordion: {
      styleOverrides: {
        root: {
          '&:before': { display: 'none' },
          borderRadius: 18,
          overflow: 'hidden',
          '&.Mui-expanded': { margin: 0 },
        },
      },
    },

    MuiAccordionSummary: {
      styleOverrides: { root: { padding: '4px 20px', '&:hover': { backgroundColor: BAND_SOFT } } },
    },

    MuiAccordionDetails: { styleOverrides: { root: { padding: '0 20px 16px' } } },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          backgroundColor: PAPER,
          fontSize: '0.8125rem',
          '& fieldset': { borderColor: LINE },
          '&:hover fieldset': { borderColor: INK_FAINT },
        },
        input: { paddingBlock: 9 },
      },
    },

    MuiInputLabel: {
      styleOverrides: { root: { fontFamily: FONT_MONO, fontSize: '0.75rem', color: INK_FAINT } },
    },

    MuiMenu: {
      styleOverrides: {
        paper: { borderRadius: 14, border: `1px solid ${LINE}`, boxShadow: STICKER_HOVER },
      },
    },

    MuiMenuItem: {
      styleOverrides: { root: { fontSize: '0.8125rem', borderRadius: 8, margin: '2px 6px' } },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 999, backgroundColor: BAND },
        bar: { borderRadius: 999 },
      },
    },

    MuiDivider: { styleOverrides: { root: { borderColor: LINE_SOFT } } },
  },
})
